
import React from 'react';
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, Clock, Briefcase, Users, Star, Eye, Calendar, Tag, Pencil, Archive, ArchiveRestore, Trash2, MoreVertical } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Job, Project } from '@/types/project';
import { Progress } from "@/components/ui/progress";
import { format } from 'date-fns';
import { DURATION_TYPE } from '@/utils/constant';
import { capitalizeFirst } from '@/lib/utils';
import { Link, useNavigate } from 'react-router-dom';
import { useUserStore } from '@/store/userStore';
import { useJobStore } from '@/store/jobStore';
import { useToast } from '@/hooks/use-toast';

interface ProjectCardProps {
  project: Job;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project }) => {

  const { user } = useUserStore();
  const { updateJobStatus, deleteJob } = useJobStore();
  const navigate = useNavigate();
  const { toast } = useToast();
  // Handle case where icon is undefined
  const displayIcon = <Briefcase size={20} />;

  // Format the complexity with appropriate color
  const getComplexityColor = (complexity: string) => {
    switch (complexity.toLowerCase()) {
      case 'low':
        return 'bg-green-100 text-green-700';
      case 'medium':
        return 'bg-amber-100 text-amber-700';
      case 'high':
        return 'bg-orange-100 text-orange-700';
      case 'very high':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'In Progress':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Planning':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const decodeHtmlEntities = (text?: string): string => {
    if (!text) return "";
    const txt = document.createElement("textarea");
    txt.innerHTML = text;
    return txt.value;
  }

  const handleArchive = async (id: string) => {
    try {
      const newVisibility = !project.projectvisibility;
      await updateJobStatus(id, newVisibility, user?.accessToken);

      toast({
        title: newVisibility ? "Project unarchived successfully" : "Project archived successfully",
        variant: "success",
      });
    } catch (err) {
      toast({
        title: "Failed to update project status",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteJob(id, user?.accessToken);
      toast({ title: "Job deleted permanently", variant: "success" });
    } catch (err) {
      toast({ title: "Failed to delete job", variant: "destructive" });
    }
  };

  const handleEdit = (id: string) => {
    navigate(`/create-job?editId=${id}`);
  };

  return (
    <Card className="h-full flex flex-col overflow-hidden border-blue-100 hover:border-emerald-300 hover:shadow-md transition-all duration-300">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2">
            <div className="bg-gradient-to-r from-sky-100 to-emerald-100 p-2 rounded-full">
              {displayIcon}
            </div>
            <div>
              <h3 className="font-semibold line-clamp-1">{capitalizeFirst(project.title)}</h3>
              {/* <p className="text-sm text-muted-foreground">{project.client}</p> */}
            </div>
          </div>
          {/* <Badge className={getStatusColor(project.status)}>
            {project.status}
          </Badge> */}
        </div>
      </CardHeader>

      <CardContent className="pb-2 flex-grow">
        {project.applicationCount !== undefined && project.applicationCount !== null && (
          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
            {project.applicationCount} Applications Received
          </span>
        )}
        <p className="text-sm line-clamp-2 my-3 text-gray-600">{decodeHtmlEntities(project?.description)}</p>

        {/* {project.progress !== undefined && (
          <div className="mb-3">
            <div className="flex justify-between text-xs mb-1">
              <span>Progress</span>
              <span>{project.progress}%</span>
            </div>
            <Progress value={project.progress} className="h-1.5" />
          </div>
        )} */}

        <div className="grid grid-cols-[7fr_5fr]  gap-y-2 text-sm mb-3">
          <div className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5 text-sky-500" />
            <span className="text-gray-600">Posted: {project.createdAt ? format(project?.createdAt, "yyyy-MM-dd") : ""}</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5 text-emerald-500" />
            {project.durationValue ?
              <span className="text-gray-600">
                {project?.durationValue} {DURATION_TYPE.find(d => Number(d.value) === project?.durationUnit)?.label}
              </span>
              :
              <span className="text-gray-600">N/A </span>
            }
          </div>
          <div className="flex items-center gap-1">
            <Briefcase className="w-3.5 h-3.5 text-sky-500" />
            <span className="font-medium">$ {project.budget}{project.PricingDetails === "dailyRate" ? "/day" : ""}</span>
          </div>
          {/* <div className="flex items-center gap-1">
            <Eye className="w-3.5 h-3.5 text-emerald-500" />
            <span className="text-gray-600">{project.views} views</span>
          </div> */}
        </div>

        <div className="flex items-center gap-2 mb-3">
          {/* <Badge variant="outline" className={getComplexityColor(project.complexity)}>
            {project.complexity} Complexity
          </Badge> */}
          {project.category && (
            <div className="flex items-center text-xs text-gray-500">
              <Tag className="w-3 h-3 mr-1" />
              {project.category}
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-1 mt-2">
          {project.requiredSkills?.slice(0, 3).map((skill, index) => (
            <Badge key={index} variant="outline" className="bg-background border-blue-100 text-xs">
              {skill}
            </Badge>
          ))}
          {project.requiredSkills?.length > 3 && (
            <Badge variant="outline" className="bg-background border-blue-100 text-xs">
              +{project.requiredSkills.length - 3}
            </Badge>
          )}
        </div>
      </CardContent>

      <CardFooter className="pt-2 border-t flex justify-end gap-2 text-xs">
        {/* <div className="flex items-center gap-1 text-muted-foreground">
          <Users className="h-3.5 w-3.5" />
          <span>{project.proposals} Proposals</span>
        </div>
        <div className="flex items-center gap-1">
          <Star className="h-3.5 w-3.5 text-amber-500" />
          <span>{project.clientInfo?.rating || "N/A"}</span>
        </div> */}
        {/* Desktop actions */}
        <div className="hidden xl:flex items-center gap-2 ml-auto">
          {user.role === "Enterprise" && (
            <Button onClick={() => handleEdit(project._id)} variant="success" size="sm" className="gap-1 h-7 px-2 text-xs">
              <Pencil className="h-3 w-3" /> Update
            </Button>
          )}
          {user.role === "Enterprise" && (
            <Button onClick={() => handleArchive(project._id)} variant="success" size="sm" className="gap-1 h-7 px-2 text-xs" >
              {project.projectvisibility ? <Archive className="h-3 w-3" /> : <ArchiveRestore className="h-3 w-3" />} {project.projectvisibility ? "Archive" : "Unarchive"}
            </Button>
          )}
          {user.role === "Enterprise" && (
            <Button onClick={() => handleDelete(project._id)} variant="success" size="sm" className="gap-1 h-7 px-2 text-xs">
              <Trash2 className="h-3 w-3" /> Delete
            </Button>
          )}
        </div>

        {/* Mobile actions */}
        {user.role === "Enterprise" && (
          <div className="flex xl:hidden w-full">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="success" size="sm" className="ml-auto gap-1 h-7 px-2 text-xs">
                  Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem onClick={() => handleEdit(project._id)} className="gap-2">
                  <Pencil className="h-3 w-3" /> Update
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleArchive(project._id)} className="gap-2">
                  {project.projectvisibility ? <Archive className="h-3 w-3" /> : <ArchiveRestore className="h-3 w-3" />} {project.projectvisibility ? "Archive" : "Unarchive"}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDelete(project._id)} className="gap-2 text-red-600">
                  <Trash2 className="h-3 w-3" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
        {user.role === "Enterprise" ?
          <Link
            to={`/job-details/${project._id}`}
            key={project._id}
            className="hover:scale-[1.01] transition-transform duration-200"
          >
            <Button variant="success" size="sm" className="gap-1 h-7 px-2 text-xs">
              Details <ArrowRight className="h-3 w-3" />
            </Button>
          </Link> :
          <Link
            to={`/project-details/${project?._id}`}
            key={project?._id}
            className="hover:scale-[1.01] transition-transform duration-200"
          >
            <Button variant="success" size="sm" className="gap-1 h-7 px-2 text-xs">
              Details <ArrowRight className="h-3 w-3" />
            </Button>
          </Link>
        }
      </CardFooter>
    </Card>
  );
};

export default ProjectCard;
